/**
 * EditRoutePage Component
 *
 * Page for editing an existing climbing route.
 * Uses a photos-first 2-tab interface matching CreateRoutePage:
 * 1. Photos & Location - Update photos and route placement on map
 * 2. Route Info - Grade, setters, colors, description, styles
 *
 * Supports saving changes with a single Save button.
 */

import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  type CSSProperties,
  type ChangeEvent,
} from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, updateDoc, deleteDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { useRoute } from '@/hooks/useRoute';
import { useGym } from '@/hooks/useGyms';
import { useGymMemberships } from '@/hooks/useGymMemberships';
import { useGymSetters, type SetterInfo } from '@/hooks/useGymSetters';
import { useRouteFormValidation } from '@/hooks/useRouteFormValidation';
import { PhotoUpload, type PhotoPreview } from '@/components/upload/PhotoUpload';
import { MapPlacementStep } from '@/components/route/MapPlacementStep';
import { SetterChipsInput } from '@/components/route/SetterChipsInput';
import {
  canEditRoute,
  V_GRADES,
  ROUTE_STYLE_CATEGORIES,
  getWallSections,
  getGradeIndex,
  type VGrade,
  type RoutePhoto,
  type RouteStyle,
  type Point,
  type GradingSystem,
  type RouteIdentification,
  type RouteSetter,
  type ColorGrade,
} from '@flash/shared';
import { FONT_GRADES, fontToV, vToFont, type FontGrade } from '@/utils/gradeConversion';

/**
 * Tab section identifiers - 2-tab structure matching CreateRoutePage
 */
type SectionId = 'photos-location' | 'route-info';

/**
 * Section status
 */
type SectionStatus = 'complete' | 'incomplete';

/**
 * Convert RoutePhoto array to PhotoPreview array for PhotoUpload component
 */
const routePhotosToPreview = (photos: RoutePhoto[]): PhotoPreview[] => {
  return photos.map((photo, index) => ({
    id: `existing_${index}_${Date.now()}`,
    previewUrl: photo.thumbnailUrl || photo.url,
    file: null,
    storagePath: '',
    downloadUrl: photo.url,
  }));
};

/**
 * Convert date to YYYY-MM-DD string for date input
 */
const dateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get today's date as YYYY-MM-DD string for date input
 */
const getTodayString = (): string => {
  return dateToString(new Date());
};

/**
 * Form data state
 */
interface RouteFormData {
  name: string;
  officialGrade: VGrade | null;
  wallSection: string;
  colorGrade: string;
  description: string;
  styles: RouteStyle[];
  mapPosition: Point | null;
  /** Route identification details (hold color, tag color) */
  identification: RouteIdentification;
  /** Date the route was set */
  setDate: string;
  /** Position on the wall (left/center/right) */
  position: 'left' | 'right' | 'center' | null;
}

/**
 * Tab configuration - photos-first flow matching CreateRoutePage
 */
const TABS: { id: SectionId; label: string }[] = [
  { id: 'photos-location', label: 'Photos & Location' },
  { id: 'route-info', label: 'Route Info' },
];

/**
 * Sort color grades by their associated grade (exact or range min)
 * Colors with 'none' association go to the end, sorted alphabetically
 */
const sortColorGradesByGrade = (colors: ColorGrade[]): ColorGrade[] => {
  return [...colors].sort((a, b) => {
    // Get the grade to sort by (exactGrade or minGrade)
    const getGrade = (c: ColorGrade): VGrade | null => {
      if (c.associationType === 'exact' && c.exactGrade) return c.exactGrade;
      if (c.associationType === 'range' && c.minGrade) return c.minGrade;
      return null;
    };

    const gradeA = getGrade(a);
    const gradeB = getGrade(b);

    // Both have grades - sort by grade index
    if (gradeA && gradeB) {
      return getGradeIndex(gradeA) - getGradeIndex(gradeB);
    }

    // Only one has a grade - put the one with grade first
    if (gradeA && !gradeB) return -1;
    if (!gradeA && gradeB) return 1;

    // Neither has a grade - sort alphabetically by displayName
    return a.displayName.localeCompare(b.displayName);
  });
};

/**
 * EditRoutePage displays the route editing form with tabbed navigation
 *
 * Features:
 * - Photos-first 2-tab interface matching CreateRoutePage
 * - Multi-setter selection
 * - Single save button for all changes
 * - Auto-detection of wall section from map position
 */
export const EditRoutePage = () => {
  const { routeId } = useParams<{ routeId: string }>();
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const { route, loading: routeLoading, error: routeError } = useRoute(routeId);
  const { gym, loading: gymLoading, error: gymError } = useGym(route?.gymId);
  const { getMembership, loading: membershipsLoading } = useGymMemberships();
  const { setters: availableSetters, loading: settersLoading } = useGymSetters(route?.gymId);

  const loading = authLoading || routeLoading || gymLoading || membershipsLoading;

  // Get user's membership at this route's gym
  const membership = route?.gymId ? getMembership(route.gymId) : null;

  // Check if user can edit this route
  const userCanEdit =
    user && route && membership ? canEditRoute(user.uid, route, membership) : false;

  // Active section/tab
  const [activeSection, setActiveSection] = useState<SectionId>('photos-location');

  // Form state
  const [formData, setFormData] = useState<RouteFormData>({
    name: '',
    officialGrade: 'V0',
    wallSection: '',
    colorGrade: '',
    description: '',
    styles: [],
    mapPosition: null,
    identification: {},
    setDate: getTodayString(),
    position: null,
  });
  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const [initialPhotosLoaded, setInitialPhotosLoaded] = useState(false);

  // Multi-setter state
  const [selectedSetters, setSelectedSetters] = useState<RouteSetter[]>([]);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [showMapPlacement, setShowMapPlacement] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showRetireConfirm, setShowRetireConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isRetiring, setIsRetiring] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Initialize form data when route loads
  useEffect(() => {
    if (route && !initialPhotosLoaded) {
      // Convert setDate from Firestore Timestamp to string
      let setDateStr = getTodayString();
      if (route.setDate) {
        if (typeof route.setDate === 'object' && 'toDate' in route.setDate) {
          setDateStr = dateToString((route.setDate as { toDate: () => Date }).toDate());
        } else if (route.setDate instanceof Date) {
          setDateStr = dateToString(route.setDate);
        }
      }

      setFormData({
        name: route.name,
        officialGrade: route.officialGrade,
        wallSection: route.wallSection,
        colorGrade: route.colorGrade,
        description: route.description ?? '',
        styles: route.styles ?? [],
        mapPosition: route.mapPosition ?? null,
        identification: route.identification ?? {},
        setDate: setDateStr,
        position: route.position ?? null,
      });

      // Initialize photos
      setPhotos(routePhotosToPreview(route.photos));
      setInitialPhotosLoaded(true);

      // Initialize setters
      if (route.setters && route.setters.length > 0) {
        setSelectedSetters(route.setters);
      } else if (route.setterId && route.setterName) {
        // Fallback to legacy single setter
        setSelectedSetters([{ id: route.setterId, name: route.setterName }]);
      }
    }
  }, [route, initialPhotosLoaded]);

  // Check if gym has a floor plan for map placement
  const hasFloorPlan = Boolean(gym?.floorPlan?.imageUrl);

  // Get gym settings with defaults
  const requireRouteName = gym?.routeRequirements?.requireRouteName ?? true;
  const requirePhoto = gym?.routeRequirements?.requirePhoto ?? false;
  const gradingSystem: GradingSystem = gym?.gradingSystem ?? 'v-scale';
  const isColorOnly = gradingSystem === 'color-only';

  /**
   * Validate Photos & Location section
   */
  const validatePhotosLocation = useCallback((): boolean => {
    // Photos are only required if gym setting requires them
    if (requirePhoto && photos.length === 0) return false;
    // Map position is required if gym has floor plan
    if (hasFloorPlan && !formData.mapPosition) return false;
    return true;
  }, [requirePhoto, photos, hasFloorPlan, formData.mapPosition]);

  /**
   * Validate Route Info section using shared hook
   */
  const { validateRouteInfo } = useRouteFormValidation(
    {
      name: formData.name,
      officialGrade: formData.officialGrade,
      identification: formData.identification,
    },
    {
      requireRouteName,
      isColorOnly,
      selectedSetters,
      gym,
    }
  );

  /**
   * Compute section completion status
   */
  const sectionStatus = useMemo((): Record<SectionId, SectionStatus> => {
    return {
      'photos-location': validatePhotosLocation() ? 'complete' : 'incomplete',
      'route-info': validateRouteInfo() ? 'complete' : 'incomplete',
    };
  }, [validatePhotosLocation, validateRouteInfo]);

  /**
   * Check if all required sections are complete for saving
   */
  const canSave = useMemo(() => {
    return (
      sectionStatus['photos-location'] === 'complete' && sectionStatus['route-info'] === 'complete'
    );
  }, [sectionStatus]);

  /**
   * Handle form field changes
   */
  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Handle photo upload completion
   * Store as PhotoPreview[] to persist across tab switches
   */
  const handlePhotoUploadComplete = (results: { url: string; storagePath: string }[]) => {
    const photoPreviews: PhotoPreview[] = results.map((result, index) => ({
      id: `uploaded_${index}_${Date.now()}`,
      previewUrl: result.url,
      file: null, // Already uploaded
      downloadUrl: result.url,
      storagePath: result.storagePath,
    }));
    setPhotos(photoPreviews);
    setPhotosUploading(false);
  };

  /**
   * Toggle a style on/off
   */
  const handleStyleToggle = (style: RouteStyle) => {
    setFormData((prev) => ({
      ...prev,
      styles: prev.styles.includes(style)
        ? prev.styles.filter((s) => s !== style)
        : [...prev.styles, style],
    }));
  };

  /**
   * Handle map position selection
   */
  const handleMapPositionSelect = (position: Point, wallSection: string | null) => {
    setFormData((prev) => ({
      ...prev,
      mapPosition: position,
      wallSection: wallSection ?? prev.wallSection,
    }));
    setShowMapPlacement(false);
  };

  /**
   * Clear a field from identification object
   */
  const clearIdentificationField = (field: 'holdColor' | 'tagColor') => {
    setFormData((prev) => {
      const { [field]: _, ...rest } = prev.identification;
      return {
        ...prev,
        identification: rest,
      };
    });
  };

  /**
   * Convert SetterInfo to RouteSetter format
   */
  const setterInfoToRouteSetter = (setter: SetterInfo): RouteSetter => ({
    id: setter.id,
    name: setter.name,
  });

  /**
   * Handle setter selection changes
   */
  const handleSetterChange = (setters: { id: string; name: string }[]) => {
    setSelectedSetters(setters as RouteSetter[]);
  };

  /**
   * Handle form submission (save changes)
   */
  const handleSubmit = async () => {
    if (photosUploading) {
      setSubmitError('Please wait for photos to finish uploading');
      return;
    }

    if (!canSave) {
      setSubmitError('Please complete all required fields');
      return;
    }

    if (!user || !gym || !routeId || !route) {
      setSubmitError('Unable to save route');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const db = getFirebaseDb();
      const routeRef = doc(db, 'routes', routeId);

      // Build identification object - only include fields that have values
      const identification: RouteIdentification = {};
      if (formData.identification.holdColor) {
        identification.holdColor = formData.identification.holdColor;
      }
      if (formData.identification.tagColor) {
        identification.tagColor = formData.identification.tagColor;
      }

      // Validate wallSection exists in gym's current floor plan regions
      const validWallSections = getWallSections(gym);
      const wallSectionToUse =
        formData.wallSection && validWallSections.includes(formData.wallSection)
          ? formData.wallSection
          : (validWallSections[0] ?? '');

      // Convert PhotoPreview[] to RoutePhoto[] for storage
      const routePhotos: RoutePhoto[] = photos.map((photo, index) => ({
        url: photo.downloadUrl ?? photo.previewUrl,
        thumbnailUrl: photo.downloadUrl ?? photo.previewUrl,
        order: index,
      }));

      const updateData: Record<string, unknown> = {
        name: formData.name.trim(),
        officialGrade: formData.officialGrade,
        colorGrade: formData.colorGrade,
        wallSection: wallSectionToUse,
        description: formData.description.trim() || null,
        styles: formData.styles,
        position: formData.position,
        photos: routePhotos,
        mapPosition: formData.mapPosition,
        setDate: Timestamp.fromDate(new Date(formData.setDate)),
        // Include identification if any fields are set
        ...(Object.keys(identification).length > 0 && { identification }),
        // Multi-setter support
        setters: selectedSetters,
        // Legacy single setter fields for backward compatibility
        setterId: selectedSetters[0]?.id ?? route.setterId,
        setterName: selectedSetters[0]?.name ?? route.setterName,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(routeRef, updateData);
      navigate(`/climb`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save route';
      console.error('Error saving route:', error);
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle retiring a route (set status to 'retired')
   */
  const handleRetire = async () => {
    if (!routeId) return;

    setIsRetiring(true);
    setSubmitError(null);

    try {
      const db = getFirebaseDb();
      const routeRef = doc(db, 'routes', routeId);
      await updateDoc(routeRef, {
        status: 'retired',
        retiredAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      navigate('/climb');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to retire route';
      console.error('Error retiring route:', error);
      setSubmitError(errorMessage);
    } finally {
      setIsRetiring(false);
      setShowRetireConfirm(false);
    }
  };

  /**
   * Handle deleting a route permanently
   */
  const handleDelete = async () => {
    if (!routeId) return;

    setIsDeleting(true);
    setSubmitError(null);

    try {
      const db = getFirebaseDb();
      const routeRef = doc(db, 'routes', routeId);
      await deleteDoc(routeRef);
      navigate('/climb');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete route';
      console.error('Error deleting route:', error);
      setSubmitError(errorMessage);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading...</p>
        </div>
      </main>
    );
  }

  // Check authentication
  if (!user) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h1 style={errorTitleStyle}>Sign In Required</h1>
          <p style={errorTextStyle}>You must be signed in to edit a route.</p>
          <Link to="/login" style={actionLinkStyle}>
            Sign In
          </Link>
        </div>
      </main>
    );
  }

  // Check route exists
  if (routeError) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h1 style={errorTitleStyle}>Error</h1>
          <p style={errorTextStyle}>{routeError}</p>
        </div>
      </main>
    );
  }

  if (!route) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h1 style={errorTitleStyle}>Route Not Found</h1>
          <p style={errorTextStyle}>{"The route you're looking for doesn't exist."}</p>
        </div>
      </main>
    );
  }

  // Check gym exists
  if (gymError || !gym) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h1 style={errorTitleStyle}>Gym Not Found</h1>
          <p style={errorTextStyle}>{"The gym for this route doesn't exist."}</p>
        </div>
      </main>
    );
  }

  // Check edit permissions
  if (!userCanEdit) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h1 style={errorTitleStyle}>Permission Denied</h1>
          <p style={errorTextStyle}>{"You don't have permission to edit this route."}</p>
          <p style={errorSubtextStyle}>Only the route setter or gym owners can edit routes.</p>
        </div>
      </main>
    );
  }

  // Render map placement overlay
  if (showMapPlacement && gym.floorPlan) {
    return (
      <div style={mapPlacementContainerStyle}>
        <MapPlacementStep
          floorPlan={gym.floorPlan}
          colorGrades={gym.colorGrades ?? []}
          selectedColor={formData.colorGrade}
          grade={formData.officialGrade ?? formData.colorGrade ?? '?'}
          onPositionSelect={handleMapPositionSelect}
          onCancel={() => setShowMapPlacement(false)}
          initialPosition={formData.mapPosition}
        />
      </div>
    );
  }

  const canSubmit = !isSubmitting && !photosUploading && canSave;

  return (
    <main style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <Link to={`/climb`} style={backButtonStyle} aria-label="Back to Climb">
          <span style={backArrowStyle}>&#8592;</span>
        </Link>
        <div>
          <h1 style={pageTitleStyle}>Edit Route</h1>
          <span style={headerSubtitleStyle}>{gym.name}</span>
        </div>
      </header>

      {/* Tab navigation */}
      <nav style={navStyle}>
        <div style={navContentStyle}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              style={{
                ...navButtonStyle,
                ...(activeSection === tab.id ? navButtonActiveStyle : {}),
              }}
            >
              {tab.label}
              {sectionStatus[tab.id] === 'complete' && (
                <span style={completionDotStyle} title="Section complete" />
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Content */}
      <div style={contentStyle}>
        {/* Photos & Location Section */}
        {activeSection === 'photos-location' && (
          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Photos & Location</h2>
            <p style={sectionDescStyle}>
              Update photos of the route{requirePhoto ? ' (required)' : ''} and its location on the
              gym map.
            </p>

            <div style={formStyle}>
              {/* Photo Upload */}
              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Route Photos {requirePhoto && <span style={requiredStyle}>*</span>}
                </label>
                <span style={helpTextStyle}>
                  Upload 1-3 photos of the route{!requirePhoto && ' (optional)'}
                </span>
                {routeId && (
                  <PhotoUpload
                    routeId={routeId}
                    initialPhotos={photos}
                    onUploadComplete={handlePhotoUploadComplete}
                    onUploadStart={() => setPhotosUploading(true)}
                    onUploadError={(error) => {
                      setPhotosUploading(false);
                      setSubmitError(error);
                    }}
                    minPhotos={1}
                    maxPhotos={3}
                    disabled={isSubmitting}
                  />
                )}
              </div>

              {/* Map Placement */}
              {hasFloorPlan && (
                <div style={fieldStyle}>
                  <label style={labelStyle}>
                    Map Location <span style={requiredStyle}>*</span>
                  </label>
                  <span style={helpTextStyle}>Place this route on the gym floor plan</span>
                  <button
                    type="button"
                    onClick={() => setShowMapPlacement(true)}
                    style={mapPlacementButtonStyle}
                    disabled={isSubmitting}
                  >
                    {formData.mapPosition ? (
                      <>
                        <span style={mapPlacementIconStyle}>&#10003;</span>
                        Location Set - Tap to Change
                      </>
                    ) : (
                      <>
                        <span style={mapPlacementIconStyle}>&#128205;</span>
                        Place on Map
                      </>
                    )}
                  </button>
                  {formData.mapPosition && formData.wallSection && (
                    <span style={mapPositionTextStyle}>Wall Section: {formData.wallSection}</span>
                  )}
                </div>
              )}

              {/* Wall Section dropdown - only show if no floor plan */}
              {!hasFloorPlan && getWallSections(gym).length > 0 && (
                <div style={fieldStyle}>
                  <label htmlFor="wallSection" style={labelStyle}>
                    Wall Section <span style={requiredStyle}>*</span>
                  </label>
                  <select
                    id="wallSection"
                    name="wallSection"
                    value={formData.wallSection}
                    onChange={handleChange}
                    style={selectStyle}
                    disabled={isSubmitting}
                  >
                    <option value="">Select wall section...</option>
                    {getWallSections(gym).map((section) => (
                      <option key={section} value={section}>
                        {section}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Route Info Section */}
        {activeSection === 'route-info' && (
          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Route Information</h2>
            <p style={sectionDescStyle}>Update the grade, setters, and appearance details.</p>

            <div style={formStyle}>
              {/* Route Name - only shown when gym requires route names */}
              {requireRouteName && (
                <div style={fieldStyle}>
                  <label htmlFor="name" style={labelStyle}>
                    Route Name <span style={requiredStyle}>*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., The Crimpy One, Route #42"
                    style={inputStyle}
                    disabled={isSubmitting}
                    maxLength={100}
                  />
                </div>
              )}

              {/* Setter Grade - only shown if not color-only mode */}
              {!isColorOnly && (
                <div style={fieldStyle}>
                  <label htmlFor="officialGrade" style={labelStyle}>
                    Setter Grade <span style={requiredStyle}>*</span>
                  </label>
                  {gradingSystem === 'font' ? (
                    <select
                      id="officialGrade"
                      name="officialGrade"
                      value={
                        formData.officialGrade ? vToFont(formData.officialGrade as VGrade) : ''
                      }
                      onChange={(e) => {
                        const fontGrade = e.target.value as FontGrade;
                        const vGrade = fontToV(fontGrade);
                        setFormData((prev) => ({ ...prev, officialGrade: vGrade }));
                      }}
                      style={selectStyle}
                      disabled={isSubmitting}
                    >
                      {FONT_GRADES.map((fontGrade) => (
                        <option key={fontGrade} value={fontGrade}>
                          {fontGrade}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select
                      id="officialGrade"
                      name="officialGrade"
                      value={formData.officialGrade ?? 'V0'}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          officialGrade: e.target.value as VGrade,
                        }));
                      }}
                      style={selectStyle}
                      disabled={isSubmitting}
                    >
                      {V_GRADES.map((grade) => (
                        <option key={grade} value={grade}>
                          {grade}
                        </option>
                      ))}
                    </select>
                  )}
                  <span style={helpTextStyle}>
                    The official difficulty grade for this route
                    {gradingSystem === 'font' && ' (displayed in Fontainebleau scale)'}
                  </span>
                </div>
              )}

              {/* Route Identification - Color selectors based on gym labeling methods */}
              {gym.colorGrades && gym.colorGrades.length > 0 && (
                <>
                  {(() => {
                    const labelingMethods = gym.labelingMethods ?? {
                      holdColors: true,
                      tagColors: false,
                      primaryMethod: 'holdColors' as const,
                      linkColors: true,
                    };
                    const bothEnabled = labelingMethods.holdColors && labelingMethods.tagColors;
                    const isLinked = bothEnabled && labelingMethods.linkColors;
                    const sortedColors = sortColorGradesByGrade(gym.colorGrades);
                    // Use separate tag colors when unlinked, otherwise same as hold colors
                    const sortedTagColors =
                      !isLinked && gym.tagColorGrades?.length
                        ? sortColorGradesByGrade(gym.tagColorGrades)
                        : sortedColors;

                    // Linked mode: single "Route Color" picker
                    if (isLinked) {
                      return (
                        <div style={fieldStyle}>
                          <label htmlFor="routeColor" style={labelStyle}>
                            Route Color <span style={requiredStyle}>*</span>
                          </label>
                          <span style={helpTextStyle}>
                            The color for this route (applies to both holds and tags)
                          </span>
                          <div style={colorGradesContainerStyle}>
                            {sortedColors.map((colorGrade) => {
                              const isSelected =
                                formData.identification.holdColor === colorGrade.displayName;
                              return (
                                <button
                                  key={colorGrade.displayName}
                                  type="button"
                                  onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      colorGrade: colorGrade.displayName,
                                      identification: {
                                        ...prev.identification,
                                        holdColor: colorGrade.displayName,
                                        tagColor: colorGrade.displayName,
                                      },
                                    }))
                                  }
                                  style={{
                                    ...colorGradeButtonStyle,
                                    backgroundColor: colorGrade.color,
                                    ...(isSelected ? colorGradeButtonSelectedStyle : {}),
                                  }}
                                  disabled={isSubmitting}
                                  title={colorGrade.displayName}
                                >
                                  {isSelected && <span style={colorGradeCheckStyle}>&#10003;</span>}
                                </button>
                              );
                            })}
                          </div>
                          <span style={helpTextStyle}>
                            Selected: {formData.identification.holdColor || 'None selected'}
                          </span>
                        </div>
                      );
                    }

                    // Unlinked mode: separate pickers
                    const holdIsRequired = labelingMethods.primaryMethod === 'holdColors';
                    const tagIsRequired = labelingMethods.primaryMethod === 'tagColors';

                    return (
                      <>
                        {/* Hold Color - shown if holdColors is enabled */}
                        {labelingMethods.holdColors && (
                          <div style={fieldStyle}>
                            <label htmlFor="holdColor" style={labelStyle}>
                              Hold Color{' '}
                              {holdIsRequired ? (
                                <span style={requiredStyle}>*</span>
                              ) : (
                                <span style={optionalStyle}>(optional)</span>
                              )}
                            </label>
                            <span style={helpTextStyle}>The color of the holds for this route</span>
                            <div style={colorGradesContainerStyle}>
                              {/* Show "None" deselect when hold color is optional */}
                              {!holdIsRequired && (
                                <button
                                  type="button"
                                  onClick={() => clearIdentificationField('holdColor')}
                                  style={{
                                    ...colorGradeButtonStyle,
                                    backgroundColor: '#e5e7eb',
                                    border: '2px dashed #9ca3af',
                                    ...(!formData.identification.holdColor
                                      ? colorGradeButtonSelectedStyle
                                      : {}),
                                  }}
                                  disabled={isSubmitting}
                                  title="No hold color"
                                >
                                  {!formData.identification.holdColor && (
                                    <span style={colorGradeCheckStyle}>&#10003;</span>
                                  )}
                                </button>
                              )}
                              {sortedColors.map((colorGrade) => {
                                const isSelected =
                                  formData.identification.holdColor === colorGrade.displayName;
                                return (
                                  <button
                                    key={colorGrade.displayName}
                                    type="button"
                                    onClick={() =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        colorGrade: colorGrade.displayName,
                                        identification: {
                                          ...prev.identification,
                                          holdColor: colorGrade.displayName,
                                        },
                                      }))
                                    }
                                    style={{
                                      ...colorGradeButtonStyle,
                                      backgroundColor: colorGrade.color,
                                      ...(isSelected ? colorGradeButtonSelectedStyle : {}),
                                    }}
                                    disabled={isSubmitting}
                                    title={colorGrade.displayName}
                                  >
                                    {isSelected && (
                                      <span style={colorGradeCheckStyle}>&#10003;</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            <span style={helpTextStyle}>
                              Selected: {formData.identification.holdColor || 'None'}
                            </span>
                          </div>
                        )}

                        {/* Tag/Tape Color - shown if tagColors is enabled */}
                        {labelingMethods.tagColors && (
                          <div style={fieldStyle}>
                            <label htmlFor="tagColor" style={labelStyle}>
                              Tag/Tape Color{' '}
                              {tagIsRequired ? (
                                <span style={requiredStyle}>*</span>
                              ) : (
                                <span style={optionalStyle}>(optional)</span>
                              )}
                            </label>
                            <span style={helpTextStyle}>
                              Color of tape or tags marking the route
                            </span>
                            <div style={colorGradesContainerStyle}>
                              {/* Show "None" deselect when tag color is optional */}
                              {!tagIsRequired && (
                                <button
                                  type="button"
                                  onClick={() => clearIdentificationField('tagColor')}
                                  style={{
                                    ...colorGradeButtonStyle,
                                    backgroundColor: '#e5e7eb',
                                    border: '2px dashed #9ca3af',
                                    ...(!formData.identification.tagColor
                                      ? colorGradeButtonSelectedStyle
                                      : {}),
                                  }}
                                  disabled={isSubmitting}
                                  title="No tags/tape"
                                >
                                  {!formData.identification.tagColor && (
                                    <span style={colorGradeCheckStyle}>&#10003;</span>
                                  )}
                                </button>
                              )}
                              {sortedTagColors.map((colorGrade) => {
                                const isSelected =
                                  formData.identification.tagColor === colorGrade.displayName;
                                return (
                                  <button
                                    key={colorGrade.displayName}
                                    type="button"
                                    onClick={() =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        identification: {
                                          ...prev.identification,
                                          tagColor: colorGrade.displayName,
                                        },
                                      }))
                                    }
                                    style={{
                                      ...colorGradeButtonStyle,
                                      backgroundColor: colorGrade.color,
                                      ...(isSelected ? colorGradeButtonSelectedStyle : {}),
                                    }}
                                    disabled={isSubmitting}
                                    title={colorGrade.displayName}
                                  >
                                    {isSelected && (
                                      <span style={colorGradeCheckStyle}>&#10003;</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            <span style={helpTextStyle}>
                              Selected: {formData.identification.tagColor || 'None'}
                            </span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}

              {/* Position on Wall */}
              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Position in Picture <span style={optionalStyle}>(optional)</span>
                </label>
                <span style={helpTextStyle}>
                  For routes that share the same color(s) in the same picture
                </span>
                <div style={positionToggleContainerStyle}>
                  {(['left', 'center', 'right'] as const).map((pos) => {
                    const isSelected = formData.position === pos;
                    const label = pos === 'left' ? 'Left' : pos === 'center' ? 'Center' : 'Right';
                    return (
                      <button
                        key={pos}
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            position: prev.position === pos ? null : pos,
                          }))
                        }
                        style={{
                          ...positionButtonStyle,
                          ...(isSelected ? positionButtonSelectedStyle : {}),
                        }}
                        disabled={isSubmitting}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Route Attributes */}
              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Route Attributes <span style={optionalStyle}>(optional)</span>
                </label>
                <span style={helpTextStyle}>Select attributes that describe this route</span>
                {ROUTE_STYLE_CATEGORIES.map((category) => (
                  <div key={category.key} style={{ marginBottom: '12px' }}>
                    <span style={categoryLabelStyle}>{category.label}</span>
                    <div style={stylesContainerStyle}>
                      {category.styles.map((styleInfo) => {
                        const isSelected = formData.styles.includes(styleInfo.id);
                        return (
                          <button
                            key={styleInfo.id}
                            type="button"
                            onClick={() => handleStyleToggle(styleInfo.id)}
                            style={{
                              ...styleChipStyle,
                              ...(isSelected ? styleChipSelectedStyle : {}),
                            }}
                            disabled={isSubmitting}
                            title={styleInfo.description}
                          >
                            {styleInfo.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Description - Hidden for now
              <div style={fieldStyle}>
                <label htmlFor="description" style={labelStyle}>
                  Description <span style={optionalStyle}>(optional)</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="General notes about the route..."
                  style={textareaStyle}
                  disabled={isSubmitting}
                  rows={3}
                  maxLength={1000}
                />
                <div style={charCountStyle}>{formData.description.length}/1000</div>
              </div>
              */}

              {/* Setters - Multi-setter selection */}
              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Setters <span style={requiredStyle}>*</span>
                </label>
                <span style={helpTextStyle}>Select who set this route.</span>
                {settersLoading ? (
                  <p style={loadingTextStyle}>Loading setters...</p>
                ) : (
                  <SetterChipsInput
                    availableSetters={availableSetters.map(setterInfoToRouteSetter)}
                    selectedSetters={selectedSetters}
                    onChange={handleSetterChange}
                    currentUserId={user.uid}
                    currentUserName={userProfile?.displayName ?? user.displayName ?? 'Unknown'}
                    disabled={isSubmitting}
                  />
                )}
              </div>

              {/* Set Date */}
              <div style={fieldStyle}>
                <label htmlFor="setDate" style={labelStyle}>
                  Set Date
                </label>
                <input
                  id="setDate"
                  name="setDate"
                  type="date"
                  value={formData.setDate}
                  onChange={handleChange}
                  style={inputStyle}
                  disabled={isSubmitting}
                />
                <span style={helpTextStyle}>When this route was set</span>
              </div>
            </div>
          </section>
        )}

        {/* Submit error */}
        {submitError && <div style={submitErrorStyle}>{submitError}</div>}
      </div>

      {/* Sticky footer with action buttons */}
      <footer style={stickyFooterStyle}>
        <div style={footerButtonsStyle}>
          {/* Danger actions */}
          <div style={dangerActionsStyle}>
            <button
              type="button"
              onClick={() => setShowRetireConfirm(true)}
              disabled={isSubmitting || isRetiring || isDeleting || route?.status === 'retired'}
              style={{
                ...dangerButtonStyle,
                ...(route?.status === 'retired' ? disabledButtonStyle : {}),
              }}
            >
              {route?.status === 'retired' ? 'Already Retired' : 'Retire'}
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSubmitting || isRetiring || isDeleting}
              style={deleteButtonStyle}
            >
              Delete
            </button>
          </div>
          {/* Save button */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || isRetiring || isDeleting}
            style={{
              ...saveButtonStyle,
              ...(canSubmit && !isRetiring && !isDeleting ? {} : disabledButtonStyle),
            }}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </footer>

      {/* Retire confirmation dialog */}
      {showRetireConfirm && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={modalTitleStyle}>Retire Route?</h3>
            <p style={modalTextStyle}>
              This will mark the route as retired. It will no longer appear in the main route list,
              but climb history will be preserved. You can restore it later.
            </p>
            <div style={modalButtonsStyle}>
              <button
                type="button"
                onClick={() => setShowRetireConfirm(false)}
                style={modalCancelButtonStyle}
                disabled={isRetiring}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRetire}
                style={modalConfirmButtonStyle}
                disabled={isRetiring}
              >
                {isRetiring ? 'Retiring...' : 'Retire Route'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={modalTitleStyle}>Delete Route Permanently?</h3>
            <p style={modalTextStyle}>
              This will permanently delete the route and all associated data. This action cannot be
              undone.
            </p>
            <div style={modalButtonsStyle}>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                style={modalCancelButtonStyle}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                style={modalDeleteButtonStyle}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

/**
 * Component styles - using CSS variables for dark mode support
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: 'var(--color-bg-secondary)',
  display: 'flex',
  flexDirection: 'column',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: 'var(--color-text-secondary)',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
  padding: '20px',
  textAlign: 'center',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: '12px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-primary)',
  marginBottom: '8px',
};

const errorSubtextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '24px',
};

const actionLinkStyle: CSSProperties = {
  display: 'inline-block',
  padding: '12px 24px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  borderRadius: '8px',
  textDecoration: 'none',
  fontWeight: 500,
};

const headerStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderBottom: '1px solid var(--color-border)',
  padding: '12px 20px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const backButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px',
  backgroundColor: 'transparent',
  color: 'var(--color-text-secondary)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  textDecoration: 'none',
  marginLeft: '-6px',
};

const backArrowStyle: CSSProperties = {
  fontSize: '1.25rem',
};

const pageTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: 0,
  lineHeight: 1.2,
};

const headerSubtitleStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
};

const navStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderBottom: '1px solid var(--color-border)',
  overflowX: 'auto',
};

const navContentStyle: CSSProperties = {
  maxWidth: '600px',
  margin: '0 auto',
  display: 'flex',
  gap: '4px',
  padding: '0 20px',
};

const navButtonStyle: CSSProperties = {
  padding: '16px 20px',
  backgroundColor: 'transparent',
  border: 'none',
  borderBottomWidth: '3px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'transparent',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flex: 1,
  justifyContent: 'center',
};

const navButtonActiveStyle: CSSProperties = {
  borderBottomColor: 'var(--color-primary)',
  color: 'var(--color-primary)',
};

const completionDotStyle: CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: 'var(--color-success)',
  display: 'inline-block',
};

const contentStyle: CSSProperties = {
  maxWidth: '600px',
  margin: '0 auto',
  padding: '20px',
  flex: 1,
  width: '100%',
  boxSizing: 'border-box',
};

const sectionStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
  padding: '24px',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginTop: 0,
  marginBottom: '8px',
};

const sectionDescStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '20px',
};

const formStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
};

const fieldStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const labelStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const requiredStyle: CSSProperties = {
  color: 'var(--color-error)',
};

const optionalStyle: CSSProperties = {
  fontWeight: 400,
  color: 'var(--color-text-muted)',
};

const inputStyle: CSSProperties = {
  padding: '12px 16px',
  fontSize: '1rem',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  outline: 'none',
  transition: 'border-color 0.2s',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
};

const selectStyle: CSSProperties = {
  padding: '12px 16px',
  fontSize: '1rem',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  outline: 'none',
  cursor: 'pointer',
};

const helpTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
};

const colorGradesContainerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
};

const colorGradeButtonStyle: CSSProperties = {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  borderWidth: '2px',
  borderStyle: 'solid',
  // Subtle border visible in both light and dark modes
  borderColor: 'var(--color-border)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.2s, border-color 0.2s',
};

const colorGradeButtonSelectedStyle: CSSProperties = {
  borderColor: 'var(--color-primary)',
  transform: 'scale(1.1)',
};

const colorGradeCheckStyle: CSSProperties = {
  color: 'white',
  fontSize: '1.25rem',
  fontWeight: 700,
  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
};

const categoryLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: '6px',
  marginTop: '8px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const stylesContainerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '4px',
};

const styleChipStyle: CSSProperties = {
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--color-border)',
  borderRadius: '20px',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const styleChipSelectedStyle: CSSProperties = {
  color: 'white',
  backgroundColor: 'var(--color-primary)',
  borderColor: 'var(--color-primary)',
};

const mapPlacementContainerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 100,
  backgroundColor: 'var(--color-bg-primary)',
};

const mapPlacementButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '14px 24px',
  fontSize: '1rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px dashed var(--color-border)',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const mapPlacementIconStyle: CSSProperties = {
  fontSize: '1.25rem',
};

const mapPositionTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
};

const submitErrorStyle: CSSProperties = {
  padding: '12px 16px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  border: '1px solid var(--color-error)',
  color: 'var(--color-badge-warning-text)',
  fontSize: '0.875rem',
  marginTop: '16px',
};

const stickyFooterStyle: CSSProperties = {
  position: 'sticky',
  bottom: 0,
  backgroundColor: 'var(--color-bg-primary)',
  borderTop: '1px solid var(--color-border)',
  padding: '16px 20px',
};

const footerButtonsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  maxWidth: '560px',
  margin: '0 auto',
};

const dangerActionsStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const dangerButtonStyle: CSSProperties = {
  padding: '12px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-warning, #f59e0b)',
  backgroundColor: 'transparent',
  border: '1px solid var(--color-warning, #f59e0b)',
  borderRadius: '8px',
  cursor: 'pointer',
};

const deleteButtonStyle: CSSProperties = {
  padding: '12px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-error, #ef4444)',
  backgroundColor: 'transparent',
  border: '1px solid var(--color-error, #ef4444)',
  borderRadius: '8px',
  cursor: 'pointer',
};

const saveButtonStyle: CSSProperties = {
  flex: 1,
  padding: '16px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: 'var(--color-primary)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const disabledButtonStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-muted)',
  cursor: 'not-allowed',
  opacity: 0.6,
};

const positionToggleContainerStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const positionButtonStyle: CSSProperties = {
  flex: 1,
  padding: '10px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  textAlign: 'center',
  outline: 'none',
};

const positionButtonSelectedStyle: CSSProperties = {
  color: 'white',
  backgroundColor: 'var(--color-primary)',
};

const modalOverlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 200,
  padding: '20px',
};

const modalContentStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  padding: '24px',
  maxWidth: '400px',
  width: '100%',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
};

const modalTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginTop: 0,
  marginBottom: '12px',
};

const modalTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.5,
  marginBottom: '24px',
};

const modalButtonsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
};

const modalCancelButtonStyle: CSSProperties = {
  padding: '10px 20px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  backgroundColor: 'transparent',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  cursor: 'pointer',
};

const modalConfirmButtonStyle: CSSProperties = {
  padding: '10px 20px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'white',
  backgroundColor: 'var(--color-warning, #f59e0b)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const modalDeleteButtonStyle: CSSProperties = {
  padding: '10px 20px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'white',
  backgroundColor: 'var(--color-error, #ef4444)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

export default EditRoutePage;
